import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Button,
  Box,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  confirmationBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '1rem',
    borderRadius: '4px',
  },
  confirmationText: {
    margin: 'auto',
    textAlign: 'center',
    fontSize: '1.1rem',
  },
}));

interface ContractData {
  dateSignature: string;
  periodDays: string;
  file: File | null;
}

interface ContractUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  contractData: ContractData;
  setContractData: (data: ContractData) => void;
  loading: boolean;
}

const ContractUploadModal = ({
  open,
  onClose,
  onSubmit,
  contractData,
  setContractData,
  loading,
}: ContractUploadModalProps) => {
  const classes = useStyles();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setContractData({
      dateSignature: '',
      periodDays: '',
      file: null,
    });
    onClose();
  };

  const handleContinue = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    onSubmit();
    setShowConfirmation(false);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Typography variant='h4'>
          {showConfirmation ? 'Confirmación de operación' : 'Carga de contrato'}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {!showConfirmation ? (
          <>
            <TextField
              variant='outlined'
              label='Fecha de firma de contrato'
              type='date'
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={contractData.dateSignature}
              onChange={(e) =>
                setContractData({
                  ...contractData,
                  dateSignature: e.target.value,
                })
              }
            />
            <FormControl fullWidth variant='outlined'>
              <InputLabel id='policy-load-days-label'>
                Días para la carga de póliza
              </InputLabel>
              <Select
                labelId='policy-load-days-label'
                label='Días para la carga de póliza'
                value={contractData.periodDays}
                onChange={(e) =>
                  setContractData({
                    ...contractData,
                    periodDays: e.target.value as string,
                  })
                }
              >
                <MenuItem value='30'>30 días</MenuItem>
                <MenuItem value='60'>60 días</MenuItem>
                <MenuItem value='90'>90 días</MenuItem>
              </Select>
            </FormControl>
            {contractData.file && (
              <Typography variant='body2'>
                Archivo seleccionado: {contractData.file.name}
              </Typography>
            )}
            <Button
              color='secondary'
              variant='contained'
              component='label'
              startIcon={<CloudUploadIcon />}
              style={{ width: 'fit-content', margin: 'auto' }}
              disabled={
                contractData.periodDays.length === 0 ||
                contractData.dateSignature.length === 0
              }
            >
              Seleccionar archivo
              <input
                type='file'
                hidden
                onChange={(e) =>
                  setContractData({
                    ...contractData,
                    file: e.target.files?.[0] || null,
                  })
                }
              />
            </Button>
          </>
        ) : (
          <Box className={classes.confirmationBox}>
            <Typography variant='h6' className={classes.confirmationText}>
              Confirma la información ingresada:
            </Typography>
            <Typography variant='body2' style={{ fontSize: '1.1rem' }}>
              Fecha de firma de contrato:{' '}
              {formatDate(contractData.dateSignature)}
            </Typography>
            <Typography variant='body2' style={{ fontSize: '1.1rem' }}>
              Documento: {contractData.file?.name}
            </Typography>
            <Typography variant='body2' style={{ fontSize: '1.1rem' }}>
              Límite de días: {contractData.periodDays} días
            </Typography>
            <Typography variant='h6' className={classes.confirmationText}>
              Fecha límite para la carga de póliza: <br />
              {formatDate(
                new Date(
                  new Date(contractData.dateSignature).getTime() +
                    parseInt(contractData.periodDays) * 24 * 60 * 60 * 1000
                ).toISOString()
              )}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color='primary'>
          Cancelar
        </Button>
        {!showConfirmation ? (
          <Button
            onClick={handleContinue}
            color='primary'
            disabled={
              loading ||
              !contractData.dateSignature ||
              !contractData.periodDays ||
              !contractData.file
            }
          >
            Continuar
          </Button>
        ) : (
          <>
            <Button onClick={handleCancel} color='primary'>
              Volver
            </Button>
            <Button onClick={handleConfirm} color='primary' disabled={loading}>
              Confirmar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContractUploadModal;
